/**
 * Frontend API client for panel/backend.
 * - Base URL from config
 * - Attaches Authorization: Bearer <token> when token exists
 * - On 401: clears token and dispatches auth:unauthorized (AuthContext logs out)
 * - On 403: throws ApiError so callers can show "Forbidden"
 */

import { getBackendUrl } from "@/config/admin";
import { API_DEFAULT_TIMEOUT_MS } from "@/config/timing";
import { getAdminToken, logoutAdmin } from "@/lib/admin";
import { isAndroidApp } from "@/lib/nfc-deep-link";

/** Runtime API base (WebView-safe when Android injects scja_api_base). */
export const getApiBase = () => getBackendUrl();

/** Fired when the server returns 401; AuthContext listens and logs out. */
export const AUTH_UNAUTHORIZED_EVENT = "auth:unauthorized";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function clearAuthAndNotify(): void {
  logoutAdmin();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** If true, do not add Authorization header (e.g. for login). Default false. */
  skipAuth?: boolean;
  /** JSON body; will be stringified and Content-Type set. */
  body?: Record<string, unknown> | unknown[] | unknown;
  /** Request timeout in ms. 0 = no timeout. Default from config. */
  timeout?: number;
  /** Number of retries for 5xx / network errors. Default 1. Set 0 to disable. */
  retry?: number;
}

/**
 * Fetch from the backend with base URL, auth token, and 401/403 handling.
 * - Path should be relative to API root, e.g. "/api/panel-users" not full URL.
 * - If body is a plain object/array, Content-Type: application/json is set.
 */
/** Normalize path for NestJS api/v1: /api/foo -> /foo when base already has /api/v1. Exported for tests. */
export function normalizePath(path: string, base: string): string {
  if (path.startsWith("http")) return path;
  if (base.includes("/api/v1") && path.startsWith("/api/") && !path.startsWith("/api/v1/")) {
    return path.replace(/^\/api\//, "/");
  }
  return path;
}

export async function apiFetch(
  path: string,
  options: RequestOptions = {}
): Promise<Response> {
  const { skipAuth = false, body, timeout = API_DEFAULT_TIMEOUT_MS, ...init } = options;
  const method = (init.method ?? "GET").toUpperCase();
  const isMutation = ["POST", "PATCH", "PUT", "DELETE"].includes(method);
  const retry = options.retry ?? (isMutation ? 0 : 1);

  const API_BASE = getBackendUrl();

  const normalizedPath = normalizePath(path, API_BASE);
  const url = normalizedPath.startsWith("http") ? normalizedPath : `${API_BASE}${normalizedPath}`;
  const headers = new Headers(init.headers);

  if (!skipAuth) {
    const token = getAdminToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  headers.set("X-Client-Platform", isAndroidApp() ? "android" : "web");

  let requestBody: BodyInit | undefined = init.body;
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || ArrayBuffer.isView(body)) {
      requestBody = body;
    } else if (typeof body === "object") {
      requestBody = JSON.stringify(body);
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
    }
  }

  let lastError: unknown;
  const maxAttempts = 1 + Math.max(0, retry);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : undefined;
    if (init.signal) {
      init.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    const { signal: _omit, ...fetchInit } = init;

    let res: Response;
    try {
      res = await fetch(url, {
        ...fetchInit,
        signal: controller.signal,
        credentials: fetchInit.credentials ?? "include",
        headers,
        body: requestBody,
      });
    } catch (err) {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new ApiError(`Request timed out after ${timeout}ms`, 0);
      }
      lastError = err;
      if (attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      throw err;
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }

    if (res.status === 401) {
      if (!skipAuth) {
        clearAuthAndNotify();
      }
      let parsed: unknown;
      try {
        const text = await res.text();
        parsed = text ? JSON.parse(text) : undefined;
      } catch {
        parsed = undefined;
      }
      const body = parsed as { message?: string } | undefined;
      const msg = body?.message ?? "Unauthorized";
      throw new ApiError(msg, 401, parsed);
    }

    if (res.status === 403) {
      let parsed: unknown;
      try {
        const text = await res.text();
        parsed = text ? JSON.parse(text) : undefined;
      } catch {
        parsed = undefined;
      }
      throw new ApiError("Forbidden", 403, parsed);
    }

    if (res.status >= 500 && attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      continue;
    }

    return res;
  }

  throw lastError ?? new ApiError("Request failed after retries", 0);
}

/**
 * Same as apiFetch but parses JSON and throws ApiError on non-2xx (including 401/403).
 * Handles 204 No Content (empty body) without calling .json().
 */
export async function apiJson<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const res = await apiFetch(path, options);
  if (res.status === 204) {
    return {} as T;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && "message" in data && typeof data.message === "string"
        ? data.message
        : null) ??
      (data && typeof data === "object" && "error" in data && typeof data.error === "string"
        ? data.error
        : null) ??
      res.statusText ??
      "Request failed";
    throw new ApiError(msg, res.status, data);
  }
  // Unwrap unified success response { ok: true, data } from backend
  if (data && typeof data === "object" && "ok" in data && data.ok === true && "data" in data) {
    return (data as { data: T }).data;
  }
  return data as T;
}
